import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import styles from './index.module.scss'
import sparkIcon_long from '../../assets/images/share/sparkIcon_hr.png'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import { useEffect, useState } from 'react'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { Button, FormControlLabel, Typography } from '@mui/material'
import ButtonWithNoHoverShadow from '../../components/ButtonWithNoHoverShadow'
import { signInUserWithEmailAndPassword } from '../../firebase/general-apis'
import { getLoginFailurePromptBasedOnErrorCode } from './helpers'
import { useLocation, useNavigate } from 'react-router-dom'
import { RESET_PASSWORD, TabNames } from '../../routers/type'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../redux/store'
import Header from '../../components/Header'
import {
    getAuth,
    browserLocalPersistence,
    sendPasswordResetEmail,
    setPersistence,
    browserSessionPersistence,
} from 'firebase/auth'
import pxToRem from '../../helpers/pxToRem'
import { LoginIconSx } from '../../components/TextField'
import { Checkbox } from '@mui/material'
import z from 'zod'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import FormInputText from '../../components/FormComponents/FormInputText'
import { FirebaseError } from 'firebase/app'
import { setUser } from '../../redux/userSlice'

const formSchema = z.object({
    email: z.string(),
    password: z.string(),
    rememberMe: z.boolean(),
})
type FormType = z.infer<typeof formSchema>

const LoginPage = () => {
    const {
        control,
        handleSubmit,
        formState: { isValid, isSubmitting },
    } = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { email: '', password: '', rememberMe: false },
    })
    const [showPassword, setShowPassword] = useState(false)
    const [errorMsg, setErrorMsg] = useState('')
    const navigate = useNavigate()
    const location = useLocation()
    const isResetPassword =
        location.pathname.split('/').at(-1) === RESET_PASSWORD
    const [showResetPassword, setShowResetPassword] = useState(isResetPassword)

    useEffect(() => {
        setShowResetPassword(isResetPassword)
    }, [isResetPassword])

    const handleClickShowPassword = () => setShowPassword((show) => !show)

    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault()
    }

    const dispatch = useDispatch()
    const onSubmit = async (data: FormType) => {
        await setPersistence(
            getAuth(),
            data.rememberMe
                ? browserLocalPersistence
                : browserSessionPersistence
        )
        // Unset User
        dispatch(setUser(undefined))
        const res = await signInUserWithEmailAndPassword(
            data.email,
            data.password
        )

        if (res.success) {
            setErrorMsg('')
        } else {
            setErrorMsg(getLoginFailurePromptBasedOnErrorCode(res.errorCode))
        }
    }

    return (
        <>
            <Box className={styles.header_wrapper}>
                <Header />
            </Box>
            <Stack className={`${styles.LoginPage} fill_screen_height`}>
                <Stack height={'100%'} className={styles.container}>
                    <Stack
                        className={`${styles.leftContainer} ${styles.leftContainer_bg}`}
                    >
                        {/* prettier-ignore */}
                        <svg width="1923" height="747" viewBox="0 0 1923 747" fill="none" xmlns="http://www.w3.org/2000/svg" > <path opacity="0.3" d="M330.383 727.906L4.43283 53.0303C-7.07317 29.2073 10.3156 0.407696 36.1876 0.437476L1565.54 2.1971C1578.15 2.21161 1589.52 9.46896 1595.13 21.0836L1919.25 692.166C1930.73 715.939 1913.44 744.691 1887.62 744.759L1066.98 746.909L360.011 746.792C347.385 746.79 335.998 739.531 330.383 727.906Z" fill="#DFF4FF" /> </svg>
                        <div className={styles.verticalLine}></div>
                        <Stack>
                            <h1>
                                Welcome back to
                                <br />
                                Spark Relief Teachers
                            </h1>
                            <p>Simplifying the CRT booking process</p>
                        </Stack>
                        {/* </Stack> */}
                    </Stack>
                    <Stack className={styles.rightContainer}>
                        {showResetPassword ? (
                            <ResetPasswordForm />
                        ) : (
                            <Stack className={styles.inputStack}>
                                <Box width={'100%'}>
                                    <img
                                        src={sparkIcon_long}
                                        alt="sparkIcon_long"
                                        className={
                                            styles['spark_longIcon--login']
                                        }
                                        onClick={() => navigate('/')}
                                    />
                                </Box>
                                <h1
                                    style={{ width: '100%', marginBottom: 0 }}
                                    id="formtitle"
                                >
                                    Log Into Your Account
                                </h1>
                                <Box
                                    className={styles.errMsg}
                                    id="login-error-box"
                                    sx={{
                                        visibility:
                                            errorMsg === ''
                                                ? 'hidden'
                                                : 'visible',
                                    }}
                                >
                                    <p>{errorMsg}</p>
                                </Box>

                                <Box sx={{ width: '100%' }}>
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <Stack
                                            sx={{ width: '100%' }}
                                            gap={pxToRem(24)}
                                        >
                                            <FormInputText
                                                name="email"
                                                control={control}
                                                sx={LoginIconSx}
                                                label={
                                                    <Stack
                                                        direction={'row'}
                                                        alignItems={'center'}
                                                        gap={pxToRem(12)}
                                                    >
                                                        {/* prettier-ignore */}
                                                        <svg width="24" height="19" viewBox="0 0 24 19" fill="none" xmlns="http://www.w3.org/2000/svg" > <path d="M3.51402 19H20.7423C22.8037 19 24 17.7888 24 15.4747V3.52533C24 1.21116 22.7931 0 20.486 0H3.25768C1.19626 0 0 1.20034 0 3.52533V15.4747C0 17.7888 1.20694 19 3.51402 19ZM10.7984 9.87308L3.0227 2.08708C3.17223 2.05464 3.34312 2.03301 3.5247 2.03301H20.4753C20.6676 2.03301 20.8385 2.05464 20.9987 2.09789L13.2336 9.87308C12.785 10.3164 12.4112 10.5111 12.016 10.5111C11.6208 10.5111 11.247 10.3056 10.7984 9.87308ZM1.99733 3.67672L7.80774 9.46215L1.99733 15.28V3.67672ZM16.2243 9.46215L22.0027 3.70916V15.2584L16.2243 9.46215ZM3.5247 16.9562C3.33244 16.9562 3.15087 16.9345 2.99065 16.9021L9.11081 10.7598L9.68758 11.3438C10.4673 12.1116 11.215 12.436 12.016 12.436C12.8064 12.436 13.5648 12.1116 14.3338 11.3438L14.9212 10.7598L21.0307 16.8913C20.8598 16.9345 20.6782 16.9562 20.4753 16.9562H3.5247Z" />{' '} </svg>
                                                        Email
                                                    </Stack>
                                                }
                                                slotProps={{
                                                    input: {
                                                        autoComplete:
                                                            'username',
                                                    },
                                                    inputLabel: {
                                                        shrink: true,
                                                    },
                                                    htmlInput: {
                                                        autoComplete:
                                                            'username',
                                                    },
                                                }}
                                                autoComplete={'username'}
                                            />

                                            <FormInputText
                                                name="password"
                                                control={control}
                                                sx={LoginIconSx}
                                                type={
                                                    showPassword
                                                        ? 'text'
                                                        : 'password'
                                                }
                                                onKeyDown={(e) => {
                                                    if (
                                                        isValid &&
                                                        e.key === 'Enter'
                                                    ) {
                                                        void handleSubmit(
                                                            onSubmit
                                                        )()
                                                    }
                                                }}
                                                label={
                                                    <Stack
                                                        direction={'row'}
                                                        alignItems={'center'}
                                                        gap={pxToRem(12)}
                                                    >
                                                        {/* prettier-ignore */}
                                                        <svg width="17" height="24" viewBox="0 0 17 24" fill="none" xmlns="http://www.w3.org/2000/svg" > <path d="M2.801 24H14.199C16.0785 24 17 23.0732 17 21.0512V12.4935C17 10.7001 16.2725 9.77332 14.7932 9.58074V6.77633C14.7932 2.20261 11.7254 0 8.5 0C5.27461 0 2.20685 2.20261 2.20685 6.77633V9.58074C0.727532 9.77332 0 10.7001 0 12.4935V21.0512C0 23.0732 0.921541 24 2.801 24ZM4.53495 6.55968C4.53495 3.75527 6.32953 2.21464 8.5 2.21464C10.6583 2.21464 12.465 3.75527 12.465 6.55968V9.54463H4.53495V6.55968ZM3.18902 21.8215C2.65549 21.8215 2.38873 21.5687 2.38873 20.9428V12.6018C2.38873 11.9639 2.65549 11.7352 3.18902 11.7352H13.8231C14.3566 11.7352 14.6113 11.9639 14.6113 12.6018V20.9428C14.6113 21.5687 14.3566 21.8215 13.8231 21.8215H3.18902Z" />{' '} </svg>
                                                        Password
                                                    </Stack>
                                                }
                                                slotProps={{
                                                    input: {
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                {' '}
                                                                <IconButton
                                                                    aria-label="toggle password visibility"
                                                                    onClick={
                                                                        handleClickShowPassword
                                                                    }
                                                                    onMouseDown={
                                                                        handleMouseDownPassword
                                                                    }
                                                                    edge="end"
                                                                >
                                                                    {' '}
                                                                    {showPassword ? (
                                                                        <VisibilityOff />
                                                                    ) : (
                                                                        <Visibility />
                                                                    )}{' '}
                                                                </IconButton>{' '}
                                                            </InputAdornment>
                                                        ),
                                                        autoComplete:
                                                            'current-password',
                                                    },
                                                    htmlInput: {
                                                        autoComplete:
                                                            'current-password',
                                                    },
                                                    inputLabel: {
                                                        shrink: true,
                                                    },
                                                }}
                                                autoComplete={
                                                    'current-password'
                                                }
                                            />
                                            <Stack
                                                width="100%"
                                                direction="row"
                                                justifyContent={'space-between'}
                                                alignItems={'center'}
                                            >
                                                <Stack
                                                    className={
                                                        styles.loginExtra
                                                    }
                                                >
                                                    <Controller
                                                        control={control}
                                                        name="rememberMe"
                                                        render={(
                                                            renderProps
                                                        ) => (
                                                            <FormControlLabel
                                                                id="login-rememberme-switch"
                                                                label="Remember me"
                                                                control={
                                                                    <Checkbox
                                                                        checked={
                                                                            renderProps
                                                                                .field
                                                                                .value
                                                                        }
                                                                        onChange={(
                                                                            e
                                                                        ) =>
                                                                            renderProps.field.onChange(
                                                                                e
                                                                                    .target
                                                                                    .checked
                                                                            )
                                                                        }
                                                                        icon={
                                                                            // prettier-ignore
                                                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#A3A3A3" /> </svg>
                                                                        }
                                                                        checkedIcon={
                                                                            // prettier-ignore
                                                                            <svg width="24" height="24" name="checkedIcon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > <g clipPath="url(#clip0_36_152)"> <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" fill="#0081A3" /> <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#0081A3" /> <path d="M18.6668 7L9.50016 16.1667L5.3335 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> </g> <defs> <clipPath id="clip0_36_152"> <rect width="24" height="24" fill="white" /> </clipPath> </defs> </svg>
                                                                        }
                                                                    />
                                                                }
                                                            />
                                                        )}
                                                    />
                                                </Stack>
                                                <ButtonWithNoHoverShadow
                                                    disableRipple
                                                    variant="text"
                                                    className={`${styles.forget_password}`}
                                                    id="forgot-password-btn"
                                                    onClick={() =>
                                                        navigate(
                                                            `/${TabNames.LOGIN}/${RESET_PASSWORD}`
                                                        )
                                                    }
                                                >
                                                    Forgot password?
                                                </ButtonWithNoHoverShadow>
                                            </Stack>
                                            <Button
                                                className={styles.submitButton}
                                                loading={isSubmitting}
                                                type="submit"
                                                variant="contained"
                                                name="loginSubmit"
                                            >
                                                {isSubmitting ? '' : 'Login'}
                                            </Button>
                                        </Stack>
                                    </form>
                                </Box>

                                <Stack className={`${styles.noAccount_stack}`}>
                                    <small>Don't have an account?</small>
                                    <p
                                        role="link"
                                        id="register-link"
                                        onClick={() =>
                                            navigate(`/${TabNames.REGISTER}`)
                                        }
                                    >
                                        Register here
                                    </p>
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </>
    )
}

const resetPasswordSchema = z.object({
    email: z.email(),
})
const ResetPasswordForm = () => {
    const screenType = useSelector((state: RootState) => state.ui.screenType)
    const navigate = useNavigate()

    const { handleSubmit, control, formState } = useForm({
        resolver: zodResolver(resetPasswordSchema),
        defaultValues: {
            email: '',
        },
    })

    const [errorMsg, setErrorMsg] = useState<string>()

    const onSubmit = async (data: z.infer<typeof resetPasswordSchema>) => {
        try {
            setErrorMsg(undefined)
            await sendPasswordResetEmail(getAuth(), data.email)
        } catch (error) {
            if (error instanceof FirebaseError) {
                if (
                    error.code === 'auth/user-not-found' ||
                    error.code === 'auth/invalid-email' ||
                    error.code === 'auth/internal-error' ||
                    error.code === 'auth/missing-email'
                ) {
                    setErrorMsg('Something went wrong.')
                }
            }
        }
    }

    return (
        <>
            {formState.isSubmitSuccessful ? (
                <Stack
                    className={`${styles.inputStack} ${styles.resetPasswordInputStack}`}
                >
                    <Box
                        className={`${screenType === 'PC' ? 'headLine_large' : 'title_large'} regular_weight ${styles.title}`}
                    >
                        Recovery Email Sent!
                    </Box>
                    <Box
                        className={`title_medium medium_weight ${styles.resetPasswordDes}`}
                    >
                        Please check your email for next steps to reset your
                        password.
                    </Box>
                    <Button
                        variant="text"
                        className={styles.primary_60}
                        onClick={() => navigate(`/${TabNames.LOGIN}`)}
                    >
                        Back to login
                    </Button>
                </Stack>
            ) : (
                <Stack
                    className={`${styles.inputStack} ${styles.resetPasswordInputStack}`}
                >
                    <Typography variant="h4">Reset your password</Typography>
                    <Typography>
                        Type in your registered email address to reset password
                    </Typography>
                    <Typography
                        className={`label_large medium_weight ${styles.resetPasswordErrMsg}`}
                        id="resetpassword-error-msg"
                        sx={{
                            display: !errorMsg ? 'none' : 'unset',
                        }}
                    >
                        {errorMsg}
                    </Typography>
                    <form
                        name="password-reset"
                        onSubmit={handleSubmit(onSubmit)}
                        style={{ width: '100%' }}
                    >
                        <Stack gap={3}>
                            <FormInputText
                                control={control}
                                type="email"
                                name="email"
                                label=""
                                placeholder={'Email Address*'}
                                autoComplete="email"
                                slotProps={{
                                    htmlInput: {
                                        autoComplete: 'email',
                                    },
                                    input: {
                                        autoComplete: 'email',
                                    },
                                }}
                                // sx={SchoolRegSx}
                            />
                            <Button
                                className={styles.submitButton}
                                loading={formState.isLoading}
                                type="submit"
                                variant="contained"
                            >
                                {formState.isLoading ? '' : 'Send reset email'}
                            </Button>
                        </Stack>
                    </form>
                    <Button
                        variant="text"
                        className={styles.primary_60}
                        onClick={() => navigate(`/${TabNames.LOGIN}`)}
                    >
                        Back to login
                    </Button>
                </Stack>
            )}
        </>
    )
}

export default LoginPage
